import React, { useState, useEffect, FormEvent, useCallback } from 'react';
import axiosInstance from 'config/axiosInstance';
import Search from 'reusable/search';
import SortBy from 'reusable/sortby';
import Pagination from 'reusable/pagination';
import AttendanceTable from './AttendanceTable';
import EditDialog from 'reusable/Dialog/editDialog';
import {
  DialogConfig,
  DialogField,
  DialogType,
  NewDataField,
} from 'reusable/Dialog/dialogInterface';
import { attendanceSortingOptions, correctionReason, editAttendanceFields } from './AttendanceField';
import MonthNavigator from 'reusable/MonthNavigator';
import { PAGE_SIZE } from 'config/constant';
import { useTranslation } from 'react-i18next';

import {
  businessDateRange,
  businessDateTimeToIsoKeeping,
  businessMonth,
  monthBounds,
} from 'config/time';
import { useSaveError } from 'reusable/useSaveError';
import { useLatestRequest } from 'reusable/useLatestRequest';
import FetchFailed from 'reusable/FetchFailed';
import { pageToRecover } from 'reusable/pageRecovery';

const asText = (value: string | number | null | undefined): string | null =>
  value === null || value === undefined ? null : String(value);

const AttendanceManagement: React.FC = () => {
  const { t } = useTranslation(['attendance', 'common']);
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('');
  // A28（2026-11-03 和田様の UAT）。表の外の月の切り替えで選んだ月（`YYYY-MM`）。以前は1日ずつ選んでいた。
  const [month, setMonth] = useState(businessMonth);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const [attendanceData, setAttendanceData] = useState<any[]>([]);
  const [count, setCount] = useState(1);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const { showSaveError, saveErrorPopup } = useSaveError();
  // ADM-15。取得の失敗（空の一覧と区別する）と、最後の要求の応答だけを反映するための番号。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();
  const [dialogProps, setDialogProps] = useState<DialogConfig>({
    title: '',
    subtitle: '',
    fields: [],
  });
  const [currentAttendanceId, setCurrentAttendanceId] = useState<string | null>(
    null
  );
  // ADM-04。編集前の値。変えていない時刻は元の値（秒・ミリ秒を含む）のまま送る。
  const [editingOriginal, setEditingOriginal] = useState<{
    check_in?: string | null;
    check_out?: string | null;
  } | null>(null);

  const fetchProfilePicture = async (userId: string) => {
    try {
      const response = await axiosInstance.get(`/v1/drivers/${userId}/`);
      return response.data.profile_picture;
    } catch (error) {
      console.error(
        `Failed to fetch profile picture for user ${userId}`,
        error
      );
      return null;
    }
  };

  const fetchAttendanceData = useCallback(async () => {
    const isCurrent = beginRequest();
    // ADM-05。業務日（日本時間）の範囲はtime.tsで作る。画面ごとに計算しない。
    const { start, end } = monthBounds(month);
    const { gte, lte } = businessDateRange(start, end);

    try {
      let apiUrl = `/v1/attendances/?check_in__gte=${encodeURIComponent(
        gte ?? ''
      )}&check_in__lte=${encodeURIComponent(
        lte ?? ''
      )}&page=${currentPage}&page_size=${PAGE_SIZE}`;
      if (sortValue) {
        // ADM-07。並び順のqueryは `order_by`。`ordering` は無視されていた。
        apiUrl += `&order_by=${encodeURIComponent(sortValue)}`;
      }
      if (searchValue) {
        apiUrl += `&search=${encodeURIComponent(searchValue)}`;
      }

      const response = await axiosInstance.get(apiUrl);
      if (!isCurrent()) return;
      // EV-460。今のページが無くなっていたら（空のページ・件数あり）、最後の有効なページを読む。
      const recover = pageToRecover(currentPage, PAGE_SIZE, {
        count: response.data.count,
        rows: response.data.results?.length ?? 0,
      });
      if (recover !== null) {
        setCurrentPage(recover);
        return;
      }
      const fetchedAttendanceData = response.data.results;

      const updatedAttendanceData = await Promise.all(
        fetchedAttendanceData.map(async (attendance: any) => {
          if (attendance.user && attendance.user.id) {
            const profilePicture = await fetchProfilePicture(attendance.user.id);
            return {
              ...attendance,
              user: { ...attendance.user, profile_picture: profilePicture },
            };
          }
          return attendance;
        })
      );

      if (!isCurrent()) return;
      setAttendanceData(updatedAttendanceData);
      setCount(response.data.count);
      setFetchError(null);
    } catch (error) {
      if (!isCurrent()) return;
      // EV-460。ページの外（404）なら、エラーにせず1ページ目を読む。
      const recoverPage = pageToRecover(currentPage, PAGE_SIZE, { error });
      if (recoverPage !== null) {
        setCurrentPage(recoverPage);
        return;
      }
      console.error('Failed to fetch data:', error);
      setFetchError(error);
    }
  }, [searchValue, month, sortValue, currentPage, beginRequest]);

  useEffect(() => {
    fetchAttendanceData();
  }, [fetchAttendanceData]);

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleMonthChange = (value: string) => {
    setMonth(value);
    setCurrentPage(1);
  };

  const openDialog = (
    title: string,
    subtitle: string,
    fields: DialogField[],
    dialogType: DialogType
  ) => {
    setDialogProps({ title, subtitle, fields });
    if (dialogType === DialogType.Edit) setIsEditDialogOpen(true);
  };

  const closeDialog = () => {
    setIsEditDialogOpen(false);
    setCurrentAttendanceId(null);
    setEditingOriginal(null);
  };

  const handleDialog = async (dialogType: DialogType, id?: string) => {
    if (dialogType === DialogType.Edit && id) {
      try {
        const attendanceResponse = await axiosInstance.get(
          `/v1/attendances/${id}/`
        );
        const attendanceData = attendanceResponse.data;
        setCurrentAttendanceId(id);
        setEditingOriginal(attendanceData);

        openDialog(
          t('edit.title'),
          '',
          editAttendanceFields(t, attendanceData),
          DialogType.Edit
        );
      } catch (error) {
        console.error('Failed to fetch attendances data', error);
        // ADM-15。開けなかった理由を出す（以前は押しても何も起きないように見えた）。
        showSaveError(error);
      }
    }
  };

  const handleEditAttendance = async (
    e: FormEvent<Element>,
    updatedData: NewDataField & { id: string }
  ) => {
    e.preventDefault();

    // ADM-04。画面の入力は日本時間。端末の現地時刻で組み立てない（config/time に揃える）。
    // 退勤が空（未退勤）のときは項目ごと送らない。以前は 'NaN-NaN-NaNTNaN:NaN:NaN.000Z' を送っていた。
    const checkInDateTime = businessDateTimeToIsoKeeping(
      asText(updatedData.check_in_date),
      asText(updatedData.check_in_time),
      editingOriginal?.check_in
    );
    const checkOutDateTime = businessDateTimeToIsoKeeping(
      asText(updatedData.check_out_date),
      asText(updatedData.check_out_time),
      editingOriginal?.check_out
    );

    const formattedUpdatedData: Record<string, string> = {};
    if (checkInDateTime) formattedUpdatedData.check_in = checkInDateTime;
    if (checkOutDateTime) formattedUpdatedData.check_out = checkOutDateTime;
    // BE-09-AC5（RA-AC3）。修正の理由（サーバーで必須。修正の前後と一緒に記録される）。
    formattedUpdatedData.reason = correctionReason(updatedData);

    try {
      if (updatedData) {
        const response = await axiosInstance.patch(
          `/v1/attendances/${updatedData.id}/`,
          formattedUpdatedData
        );

        setAttendanceData((prevData) =>
          prevData.map((attendance) =>
            attendance.id === updatedData.id ? response.data : attendance
          )
        );
      }

      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update attendance:', error);
      // 訂正が拒否された理由（退勤が出勤より前など）を表示する。
      // ADM-15。ダイアログは開いたまま（入力を残したまま）理由を前面に出す。
      showSaveError(error);
    }
  };
  return (
    <div>
      {/* EV-309。1024px 未満では、ツールバーの右の組を次の行へ折り返し、右に寄せる。1024px 以上は1行のまま（検索の欄が縮む）。
          「超過勤務のみ」の文字は折らない（1024px で縦に1文字ずつ折れていた）。 */}
      <div className="flex flex-row max-lg:flex-wrap gap-y-2 justify-between">
        <div className="flex w-2/5 justify-start">
          <Search
            placeholder={t('table.search')}
            onSearchChange={handleSearchChange}
          />
        </div>
        <div className="ml-auto flex flex-row space-x-4 items-center">
          <SortBy
            options={attendanceSortingOptions(t)}
            defaultValue=""
            onChange={handleSortChange}
          />
          <EditDialog
            isOpen={isEditDialogOpen}
            onClose={closeDialog}
            handleSubmit={(e: any, updatedData: any) =>
              handleEditAttendance(e, {
                ...updatedData,
                id: currentAttendanceId ?? '',
              })
            }
            title={dialogProps.title}
            subtitle={dialogProps.subtitle ?? ''}
            confirmLabel={t('edit.save')}
            cancelLabel={t('edit.cancel')}
            fields={dialogProps.fields}
            attendanceId={currentAttendanceId ?? ''}
          />
          <MonthNavigator month={month} onChange={handleMonthChange} />
          {/* 2026-11-03 UAT A22（和田様）。「超過勤務のみ」の絞り込みは、超過時間をなくしたため外した。 */}
        </div>
      </div>

      {fetchError ? (
        <FetchFailed error={fetchError} onRetry={fetchAttendanceData} />
      ) : (
        <AttendanceTable
          attendances={attendanceData}
          onEdit={handleDialog.bind(null, DialogType.Edit)}
        />
      )}

      <div>
        <Pagination
          allRows={count}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
      {saveErrorPopup}
    </div>
  );
};

export default AttendanceManagement;